import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  DialogContentText,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { companiesAPI } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { AutoCloseAlert } from '../Common';

const CustomToolbar = ({ onAdd, hasCreatePermission, hasExportPermission }) => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    {hasExportPermission && (
      <GridToolbarExport 
        printOptions={{ disableToolbarButton: true }}
        csvOptions={{ 
          disableToolbarButton: false,
          fileName: 'Companies' // Custom filename for CSV export
        }}
      />
    )}
    {hasCreatePermission && (
      <Button startIcon={<AddIcon />} onClick={onAdd}>
        Add Company
      </Button>
    )}
  </GridToolbarContainer>
);

const Companies = () => {
  const { hasPermission } = usePermissions();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    website: '',
    phone: '',
    email: '',
    is_active: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const canView = hasPermission('Companies', 'view');
  const canCreate = hasPermission('Companies', 'create');
  const canUpdate = hasPermission('Companies', 'update');
  const canDelete = hasPermission('Companies', 'delete');
  const canExport = hasPermission('Companies', 'export');

  useEffect(() => {
    if (canView) {
      loadCompanies();
    }
  }, [canView, paginationModel]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await companiesAPI.getAll({
        page: paginationModel.page + 1, // Backend expects 1-based page numbers
        per_page: paginationModel.pageSize,
      });
      setCompanies(response.data.data || []);
      setRowCount(response.data.total || 0);
    } catch (error) {
      console.error('Error loading companies:', error);
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCompany(null);
    setViewMode(false);
    setFormData({
      company_name: '',
      website: '',
      phone: '',
      email: '',
      is_active: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    setViewMode(false);
    setFormData({
      company_name: company.company_name,
      website: company.website || '',
      phone: company.phone || '',
      email: company.email || '',
      is_active: company.is_active,
    });
    setDialogOpen(true);
  };

  const handleView = (company) => {
    setEditingCompany(company);
    setViewMode(true);
    setFormData({
      company_name: company.company_name,
      website: company.website || '',
      phone: company.phone || '',
      email: company.email || '',
      is_active: company.is_active,
    });
    setDialogOpen(true);
  };

  const handleDelete = (company) => {
    setCompanyToDelete(company);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (companyToDelete) {
      try {
        await companiesAPI.delete(companyToDelete.id);
        setSuccess('Company deleted successfully');
        setDeleteDialogOpen(false);
        setCompanyToDelete(null);
        
        // Check if we need to go back to previous page if current page becomes empty
        const currentPageStartIndex = paginationModel.page * paginationModel.pageSize;
        const totalItemsAfterDelete = rowCount - 1;
        
        if (currentPageStartIndex >= totalItemsAfterDelete && paginationModel.page > 0) {
          setPaginationModel(prev => ({ ...prev, page: prev.page - 1 }));
        } else {
        loadCompanies();
        }
      } catch (error) {
        setError('Failed to delete company');
        setDeleteDialogOpen(false);
        setCompanyToDelete(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCompanyToDelete(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate phone number
    if (formData.phone) {
      const cleanedPhone = formData.phone.replace(/\D/g, '');
      if (cleanedPhone.length > 10) {
        setError('Phone number must be 10 digits or less');
        return;
      }
    }

    if (!formData.company_name.trim()) {
      setError('Company name is required');
      return;
    }

    try {
      if (editingCompany) {
        await companiesAPI.update(editingCompany.id, formData);
        setSuccess('Company updated successfully');
        setDialogOpen(false);
        loadCompanies(); // Reload current page
      } else {
        await companiesAPI.create(formData);
        setSuccess('Company created successfully');
        setDialogOpen(false);
        
        // For new companies, go to first page to see the new item
        setPaginationModel(prev => ({ ...prev, page: 0 }));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Operation failed';
      setError(errorMessage);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError('');
    setEditingCompany(null);
    setViewMode(false);
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'company_name',
      headerName: 'Company Name',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
          {params.value}
        </Box>
      ),
    },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'phone', headerName: 'Phone', width: 150 },
    { field: 'website', headerName: 'Website', width: 200 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      type: 'string',
      valueGetter: (params) => {
        // Return "Active" or "Inactive" text for filtering instead of boolean
        return params.value ? 'Active' : 'Inactive';
      },
      renderCell: (params) => {
        // Use the raw row data to get the actual boolean value
        const isActive = params.row.is_active;
        return (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      type: 'string',
      valueGetter: (params) => {
        if (!params.value) return 'N/A';
        const date = new Date(params.value);
        if (isNaN(date.getTime())) return 'N/A';
        
        // Format date with leading zeros to show DD/MM/YYYY
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
      },
      sortComparator: (v1, v2) => {
        if (v1 === 'N/A' && v2 === 'N/A') return 0;
        if (v1 === 'N/A') return -1;
        if (v2 === 'N/A') return 1;
        
        // Convert DD/MM/YYYY back to Date objects for comparison
        const [day1, month1, year1] = v1.split('/').map(Number);
        const [day2, month2, year2] = v2.split('/').map(Number);
        
        const date1 = new Date(year1, month1 - 1, day1);
        const date2 = new Date(year2, month2 - 1, day2);
        
        return date1.getTime() - date2.getTime();
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => {
        const actions = [];
        
        if (canView) {
          actions.push(
            <Tooltip title="View Company Details" arrow>
              <GridActionsCellItem
                icon={<ViewIcon />}
                label="View"
                onClick={() => handleView(params.row)}
              />
            </Tooltip>
          );
        }
        
        if (canUpdate) {
          actions.push(
            <Tooltip title="Edit Company" arrow>
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                onClick={() => handleEdit(params.row)}
              />
            </Tooltip>
          );
        }
        
        if (canDelete) {
          actions.push(
            <Tooltip title="Delete Company" arrow>
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => handleDelete(params.row)}
              />
            </Tooltip>
          );
        }
        
        return actions;
      },
    },
  ];

  if (!canView) {
    return (
      <Box p={3}>
        <Alert severity="error">
          You don't have permission to view companies.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Companies Management
      </Typography>
      
      <AutoCloseAlert
        severity="error"
        message={error}
        show={!!error}
        onClose={() => setError('')}
        autoCloseDelay={6000}
      />
      
      <AutoCloseAlert
        severity="success"
        message={success}
        show={!!success}
        onClose={() => setSuccess('')}
        autoCloseDelay={4000}
      />

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={companies}
          columns={columns}
          loading={loading}
          rowCount={rowCount}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableSelectionOnClick
          components={{
            Toolbar: () => <CustomToolbar onAdd={handleAdd} hasCreatePermission={canCreate} hasExportPermission={canExport} />
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {viewMode ? 'View Company' : editingCompany ? 'Edit Company' : 'Add New Company'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <TextField
              autoFocus
              margin="dense"
              label="Company Name"
              fullWidth
              variant="outlined"
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              required
              disabled={viewMode}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="dense"
              label="Email"
              fullWidth
              variant="outlined"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={viewMode}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="dense"
              label="Phone"
              fullWidth
              variant="outlined"
              value={formData.phone}
              onChange={(e) => {
                // Allow only numbers and basic formatting characters
                const value = e.target.value.replace(/[^\d-+() ]/g, '');
                setFormData({ ...formData, phone: value });
              }}
              disabled={viewMode}
              helperText="Enter up to 10 digits"
              error={formData.phone && formData.phone.replace(/\D/g, '').length > 10}
              sx={{ mb: 2 }}
            />



            <TextField
              margin="dense"
              label="Website"
              fullWidth
              variant="outlined"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              disabled={viewMode}
              placeholder="https://example.com"
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Active Status"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              {viewMode ? 'Close' : 'Cancel'}
            </Button>
            {!viewMode && (
              <Button type="submit" variant="contained">
                {editingCompany ? 'Update' : 'Create'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: 'error.main', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DeleteIcon />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Are you sure you want to delete the company{' '}
            <strong>{companyToDelete?.company_name}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
            This action cannot be undone. The company will be permanently removed from the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleCancelDelete}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 100 }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Companies; 